/**
 * Fixtures — `/fixtures` (module 03).
 *
 * The complete schedule of both competitions, mirroring TMS, with each fixture carrying
 * exactly one of the eight TMS match statuses. It is the Technical Delegate's answer to
 * "where is everything up to", and the readiness check that catches the one thing which
 * stops a match being played at all — a missing team colours assignment.
 *
 * **Every fixture is listed, including matches already made official.** That is printed on
 * the page because it is a design decision rather than an oversight: this screen mirrors what
 * TMS shows, and a schedule that quietly hides finished matches is a schedule the TD has to
 * hold a second, private version of in his head.
 *
 * **One fetch, and every filter is applied in the browser.** `GET /fixtures` takes a
 * competition and a day, and this page asks it for neither. The status key doubles as the
 * status filter and its eight counts recompute against the competition tab, so a filter that
 * went to the wire would empty the counts to answer the list, and would blank the whole
 * schedule for the length of a round trip every time a chip was pressed. The tournament is
 * eighty fixtures; it is held once and narrowed as often as the TD likes.
 *
 * **Nothing on this page is hard-coded from the mockup's demo tournament.** The dates in the
 * heading, the pitch beside each day, the competition numbers in the panel note and all five
 * headline figures are derived from the fixtures actually loaded, and each is omitted rather
 * than invented when the data cannot support it — a schedule with no pitch recorded says
 * nothing about a pitch instead of printing `Pitch 1`.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { fetchFixtures } from "../api/client.js";
import type { FixtureListBody, FixtureRowView } from "../api/types.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { FailBanner, Skeleton } from "../app/states.js";
import { tournamentDay } from "../app/tournamentTime.js";
import { useAsync } from "../app/useAsync.js";
import { COMPETITION_TABS, Tabs } from "../features/appointments/Tabs.js";
import type { CompetitionFilter } from "../features/appointments/derive.js";
import type { TmsMatchStatus } from "@hockeytd/shared";
import { tdMatchDebriefPath } from "./TdMatchDebriefPage.js";
import { matchHasLikelyEnded } from "./um/matchDebrief.js";

/**
 * The eight statuses in the order the specification fixes (§4), which is the order the key
 * strip prints them in and the order the tooltip numbers count.
 *
 * Statuses 1–4 describe the match itself and take precedence on the day; 5–8 describe how
 * ready the match is beforehand. The API decides which one a fixture carries — this table
 * only says what each is called and what it means, so the page can never disagree with TMS
 * about a match's state.
 */
interface StatusMeta {
  code: TmsMatchStatus;
  label: string;
  hint: string;
}

export const STATUSES: readonly StatusMeta[] = [
  { code: "official", label: "Official", hint: "TMS Match Result Official" },
  {
    code: "completed",
    label: "Completed",
    hint: "Match Finished — Not Yet Made Official By The TD Or TO On TMS",
  },
  { code: "live", label: "Live", hint: "Match On-Going" },
  { code: "warmup", label: "Warm Up", hint: "TMS Match Status: Warm Up" },
  {
    code: "published",
    label: "Published",
    hint: "All Appointments And Team Colours Confirmed And Published",
  },
  {
    code: "appointing",
    label: "Appointing",
    hint: "Some Or All Appointments Still Marked Draft On TMS",
  },
  {
    code: "colours",
    label: "Colours Published",
    hint: "Team Colours Assigned And Published — No Appointments Yet",
  },
  {
    code: "colmiss",
    label: "Colour Missing",
    hint: "Team Colours Assignment Missing — The Match Cannot Be Played",
  },
];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * `2026-09-23` → `Wed 23 Sep`.
 *
 * Parsed as UTC and read back in UTC, so the label a date carries never depends on where the
 * laptop thinks it is: a match day is a calendar day in the host city, not an instant.
 */
export function dayLabel(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return `${WEEKDAYS[date.getUTCDay()] ?? ""} ${String(date.getUTCDate())} ${MONTHS[date.getUTCMonth()] ?? ""}`;
}

/**
 * Today on the TOURNAMENT's clock, in the same `YYYY-MM-DD` shape a fixture carries.
 *
 * **RUN 83 — it used to be today as this DEVICE reckons it** (`now.getDate()` and its
 * siblings), which is the same defect RUN 82G took out of the Command Desk's own `todayIso`.
 * The number is compared straight against a fixture's `date`, and that date is a calendar day
 * in the host city: on a laptop still set to London, a Tokyo match morning is the previous
 * evening, so the whole of the day's play was marked as tomorrow's and yesterday's finished
 * matches wore Today. `dayLabel` two functions up says the rule this one was breaking.
 */
export function todayIso(now: Date = new Date()): string {
  return tournamentDay(now);
}

/** The distinct pitches the schedule names, in first-seen order. Empty when none is recorded. */
export function pitchesOf(fixtures: readonly FixtureRowView[]): string[] {
  const seen: string[] = [];
  for (const fixture of fixtures) {
    if (fixture.pitch !== null && !seen.includes(fixture.pitch)) seen.push(fixture.pitch);
  }
  return seen;
}

/**
 * The heading's grey half — `· Fri 18 Sep – Sat 3 Oct 2026 · Pitch 1`.
 *
 * Both clauses are derived and both are dropped when the data cannot support them. The year
 * is printed once, on the closing date, exactly as the mockup draws it.
 */
export function scheduleSubtitle(fixtures: readonly FixtureRowView[]): string {
  if (fixtures.length === 0) return "";
  const days = [...new Set(fixtures.map((fixture) => fixture.date))].sort();
  const first = days[0] ?? "";
  const last = days[days.length - 1] ?? "";
  const year = last.slice(0, 4);
  const window =
    first === last ? `${dayLabel(first)} ${year}` : `${dayLabel(first)} – ${dayLabel(last)} ${year}`;
  const pitches = pitchesOf(fixtures);
  return pitches.length === 0 ? `· ${window}` : `· ${window} · ${pitches.join(", ")}`;
}

/**
 * The All Fixtures panel note — `Men's #1916 And Women's #1917 · Replicates TMS`.
 *
 * The competition numbers are the ones the loaded fixtures actually carry, so a build pointed
 * at a different tournament names that tournament's competitions rather than this one's.
 */
export function competitionsNote(fixtures: readonly FixtureRowView[]): string {
  const men = fixtures.find((fixture) => fixture.competitionGender === "m")?.competitionId;
  const women = fixtures.find((fixture) => fixture.competitionGender === "w")?.competitionId;
  const parts: string[] = [];
  if (men !== undefined) parts.push(`Men's #${men}`);
  if (women !== undefined) parts.push(`Women's #${women}`);
  return parts.length === 0 ? "Replicates TMS" : `${parts.join(" And ")} · Replicates TMS`;
}

/** The pitch clause on a day heading — omitted, never guessed, when the day names none. */
function dayPitch(dayFixtures: readonly FixtureRowView[]): string {
  const pitches = pitchesOf(dayFixtures);
  return pitches.length === 0 ? "" : ` · ${pitches.join(", ")}`;
}

/** How the two halves of a fixture are named: the team, or the position it will be filled from. */
function side(code: string | null, placeholder: string | null): string {
  return code ?? placeholder ?? "?";
}

/** Sort order for the whole list: date, then kick-off, then competition (Men's before Women's). */
function byKickOff(a: FixtureRowView, b: FixtureRowView): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  if (a.time !== b.time) return a.time < b.time ? -1 : 1;
  if (a.competitionGender !== b.competitionGender) return a.competitionGender < b.competitionGender ? -1 : 1;
  return a.displayMatchNo < b.displayMatchNo ? -1 : 1;
}

/** One status chip — the same mark in the key strip and in the table's last column. */
function StatusChip({ code }: { code: TmsMatchStatus }): React.JSX.Element {
  const meta = STATUSES.find((entry) => entry.code === code);
  return (
    <span className={`fchip fchip--${code}`} title={meta?.hint ?? code}>
      <i className="fdot" aria-hidden="true" />
      {meta?.label ?? code}
    </span>
  );
}

export function FixturesPage(): React.JSX.Element {
  const schedule = useAsync<FixtureListBody>(() => fetchFixtures(), []);
  const [competition, setCompetition] = useState<CompetitionFilter>("all");
  const [status, setStatus] = useState<TmsMatchStatus | "all">("all");

  const all: readonly FixtureRowView[] = schedule.status === "ready" ? schedule.data.fixtures : [];
  const today = todayIso();

  /** The five headline figures, counted from the whole schedule rather than from the view. */
  const summary = useMemo(() => {
    const tally = (code: TmsMatchStatus): number =>
      all.filter((fixture) => fixture.tmsStatus === code).length;
    return {
      total: all.length,
      official: tally("official"),
      completed: tally("completed"),
      live: tally("live"),
      colmiss: tally("colmiss"),
    };
  }, [all]);

  /** The key strip's eight counts, which follow the competition tab and not the status filter. */
  const inCompetition = useMemo(
    () =>
      all.filter(
        (fixture) => competition === "all" || fixture.competitionGender === competition,
      ),
    [all, competition],
  );

  const rows = useMemo(
    () =>
      [...inCompetition]
        .filter((fixture) => status === "all" || fixture.tmsStatus === status)
        .sort(byKickOff),
    [inCompetition, status],
  );

  /** The rows grouped into the days they are played on, in order. */
  const days = useMemo(() => {
    const grouped: { date: string; fixtures: FixtureRowView[] }[] = [];
    for (const fixture of rows) {
      const last = grouped[grouped.length - 1];
      if (last !== undefined && last.date === fixture.date) last.fixtures.push(fixture);
      else grouped.push({ date: fixture.date, fixtures: [fixture] });
    }
    return grouped;
  }, [rows]);

  /**
   * `Jump To Today` clears any status filter first, because a filter that hides today would
   * otherwise leave the button pressing at nothing. Where the tournament has not reached
   * today — or has passed it — the first day in view is the honest destination.
   */
  function jumpToToday(): void {
    setStatus("all");
    window.requestAnimationFrame(() => {
      const target =
        document.getElementById("todayRow") ?? document.querySelector(".fixtable2 tr.dayhead");
      if (target instanceof HTMLElement) target.scrollIntoView({ block: "center" });
    });
  }

  return (
    <>
      <div className="fixband">
        <h2>
          Match Schedule <span className="md">{scheduleSubtitle(all)}</span>
        </h2>
        <HowToUse id="fixtures" title="How to use Fixtures">
          <li>
            Read the <HowToLink to="status-key">TMS Match Status Key</HowToLink> first. Each of
            the eight statuses carries the number of matches holding it, and{" "}
            <b>Colour Missing</b> is the one to act on — without a published team colours
            assignment the match cannot be played.
          </li>
          <li>
            Press any status in the key to show only those matches. Press the same status again
            to clear the filter and get the whole schedule back.
          </li>
          <li>
            Narrow to one competition with the <HowToLink to="competition-filter">Competition</HowToLink>{" "}
            tabs — <b>All</b>, <b>Men&rsquo;s</b>, <b>Women&rsquo;s</b>. The eight counts in the
            key recount themselves against whichever competition you are in.
          </li>
          <li>
            Press <HowToLink to="jump-today">Jump To Today</HowToLink> to clear the status
            filter and go straight to today&rsquo;s matches, which carry a <b>Today</b> badge on
            their day heading.
          </li>
          <li>
            Scan <HowToLink to="fixture-table">All Fixtures</HowToLink> day by day. A match still
            waiting on its teams shows the position it will be filled from — <b>1st Pool A</b>,{" "}
            <b>Winner M32</b> — and is drawn back until the pools resolve it.
          </li>
          <li>
            Check the <HowToLink to="fixtures-shown">Fixtures Shown</HowToLink> count under the
            list to see how much of the schedule your filters are hiding.
          </li>
        </HowToUse>
        <span className="spacer" />
        <span className="count">
          <b>{summary.total}</b> fixtures
          <span className="sep">·</span>
          <b>{summary.official}</b> official
          <span className="sep">·</span>
          <b>{summary.completed}</b> awaiting officialisation
          <span className="sep">·</span>
          <b>{summary.live}</b> live now
          <span className="sep">·</span>
          <b style={{ color: "var(--alert)" }}>{summary.colmiss}</b> colour missing
        </span>
      </div>

      {schedule.status === "error" ? <FailBanner error={schedule.error} /> : null}

      <section className="panel panel--d1" aria-labelledby="keyTitle">
        <div className="panel__head">
          <h3 className="panel__title" id="keyTitle">
            TMS Match Status Key
          </h3>
          <span className="panel__note">Click Any Status To Filter The List</span>
          <span className="spacer" />
          <button className="gbtn" type="button" data-howto="jump-today" onClick={jumpToToday}>
            Jump To Today
          </button>
        </div>
        <div className="keystrip" data-howto="status-key">
          {STATUSES.map((entry, index) => {
            const on = status === entry.code;
            const dimmed = status !== "all" && !on;
            const count = inCompetition.filter(
              (fixture) => fixture.tmsStatus === entry.code,
            ).length;
            return (
              <button
                key={entry.code}
                type="button"
                className={`keybtn${on ? " is-on" : ""}${dimmed ? " is-off" : ""}`}
                aria-pressed={on}
                title={`${String(index + 1)} · ${entry.hint}`}
                onClick={() => {
                  setStatus((current) => (current === entry.code ? "all" : entry.code));
                }}
              >
                <StatusChip code={entry.code} />
                <span className="kcount">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="keyhint">
          <b>One status per match.</b> Statuses 1–4 describe the match itself and take
          precedence on the day; statuses 5–8 describe how ready the match is beforehand.{" "}
          <b>Colour Missing outranks every appointment status</b> — without a published team
          colours assignment the match cannot be played.
        </div>
      </section>

      <section className="panel panel--d2" aria-labelledby="fixTitle" style={{ marginTop: 22 }}>
        <div className="panel__head">
          <h3 className="panel__title" id="fixTitle">
            All Fixtures
          </h3>
          <span className="panel__note">{competitionsNote(all)}</span>
          <span className="spacer" />
          <span className="filtlab" data-howto="competition-filter">
            Competition
          </span>
          <Tabs
            label="Competition filter"
            options={COMPETITION_TABS}
            value={competition}
            onChange={setCompetition}
            tint="competition"
          />
        </div>

        {schedule.status === "loading" ? (
          <Skeleton rows={12} />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              className="mtable fixtable2"
              aria-label="All fixtures, grouped by day"
              data-howto="fixture-table"
            >
              <thead>
                <tr>
                  <th scope="col">Match #</th>
                  <th scope="col">Time</th>
                  <th scope="col">Competition</th>
                  <th scope="col">Fixture</th>
                  <th scope="col" className="hide-sm">
                    Pool / Stage
                  </th>
                  <th scope="col">TMS Status</th>
                </tr>
              </thead>
              <tbody>
                {days.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="emptystate">
                        <b>No Fixtures Match This Filter</b>
                        Clear the status filter or switch competition to see the rest of the
                        schedule.
                      </div>
                    </td>
                  </tr>
                ) : null}

                {days.map((group) => (
                  <FixtureDay
                    key={group.date}
                    date={group.date}
                    fixtures={group.fixtures}
                    isToday={group.date === today}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="panel__foot">
          <b>Every fixture is listed, including matches already made official —</b> this page
          mirrors what TMS shows, it does not hide finished matches. Scores appear once a match
          has been played; a match still carrying <b>Completed</b> is waiting for the TD or TO
          to make the result official on TMS.
        </div>
        <div className="pager" aria-label="Fixture list count">
          <span className="pager__info num" data-howto="fixtures-shown">
            {rows.length} Of {summary.total} Fixtures Shown
          </span>
        </div>
      </section>
    </>
  );
}

/**
 * One day of the schedule: its heading row and the matches under it.
 *
 * A fragment rather than a component boundary in the table, because a `<tbody>` may hold rows
 * and nothing else — the grouping is a heading row, not a nested table.
 */
function FixtureDay({
  date,
  fixtures,
  isToday,
}: {
  date: string;
  fixtures: readonly FixtureRowView[];
  isToday: boolean;
}): React.JSX.Element {
  const count = fixtures.length;
  return (
    <>
      <tr
        className={isToday ? "dayhead dayhead--today" : "dayhead"}
        {...(isToday ? { id: "todayRow" } : {})}
      >
        <td colSpan={6}>
          {dayLabel(date)} &nbsp;
          <span className="cnt">
            · {count} {count === 1 ? "match" : "matches"}
            {dayPitch(fixtures)}
          </span>
          {isToday ? <span className="todaytag">Today</span> : null}
        </td>
      </tr>
      {fixtures.map((fixture) => (
        <FixtureRow key={fixture.tmsMatchDbId} fixture={fixture} />
      ))}
    </>
  );
}

function FixtureRow({ fixture }: { fixture: FixtureRowView }): React.JSX.Element {
  const men = fixture.competitionGender === "m";
  const done = fixture.tmsStatus === "official" || fixture.tmsStatus === "completed";
  const live = fixture.tmsStatus === "live";
  // Gap S12: a knockout fixture whose pools have not resolved yet has no teams, only the
  // position each side will be filled from. It is listed, and it is drawn back.
  const unresolved = fixture.homeTeamCode === null || fixture.awayTeamCode === null;
  const scored = fixture.homeScore !== null && fixture.awayScore !== null;
  /** Whether a post-match debrief could have been held on this fixture yet — see the link below. */
  const debriefable = done || matchHasLikelyEnded(fixture);

  const rowClass = [done ? "is-done" : "", live ? "is-live" : "", unresolved ? "is-placeholder" : ""]
    .filter((entry) => entry !== "")
    .join(" ");

  return (
    <tr
      data-comp={fixture.competitionGender}
      data-testid={`fixture-${String(fixture.tmsMatchDbId)}`}
      {...(rowClass === "" ? {} : { className: rowClass })}
    >
      <td className="cell-mno">
        {live ? (
          <span className="mno-wrap">
            {fixture.displayMatchNo}
            <span className="livechip" aria-label="Live now">
              <span className="ld" aria-hidden="true" />
              LIVE
            </span>
          </span>
        ) : (
          fixture.displayMatchNo
        )}
        {/*
         * RUN 75 — the Control Centre's one way in to the Technical Delegate's READ-ONLY Match
         * Debrief (spec 13 §9).
         *
         * It is here, in the match-number cell of the fixture it is about, because the debrief
         * screen is about ONE match and this table is the Control Centre's own list of matches:
         * the entry point that needed no new column, no new module-strip entry and no new panel
         * on a screen whose subject is something else. It costs one line under a match number.
         *
         * **It is drawn only once a debrief could have happened** — `matchHasLikelyEnded` is the
         * UM app's own 100-minute reckoning (spec 13 §1), widened to include any fixture TMS has
         * already marked finished. A link on tomorrow's pool match would lead to a screen whose
         * only honest content is `Nothing has been released yet`, on every row of the schedule.
         *
         * The label names the destination rather than the act, per gap J7: a Technical Delegate
         * reading `Debrief ›` under `M07` knows which match's debrief he is about to open.
         */}
        {debriefable ? (
          <Link
            className="fxdebrief"
            to={tdMatchDebriefPath(fixture.tmsMatchDbId)}
            data-testid={`fixture-debrief-${String(fixture.tmsMatchDbId)}`}
          >
            Debrief <span aria-hidden="true">›</span>
          </Link>
        ) : null}
      </td>
      <td className="cell-time">{fixture.time}</td>
      <td>
        <span className={men ? "comp-tag" : "comp-tag comp-tag--w"}>{men ? "MEN" : "WOMEN"}</span>
      </td>
      <td className="cell-teams">
        {side(fixture.homeTeamCode, fixture.homePlaceholder)}
        {scored ? (
          <span className="score">
            {fixture.homeScore}–{fixture.awayScore}
          </span>
        ) : (
          <span className="v">v</span>
        )}
        {side(fixture.awayTeamCode, fixture.awayPlaceholder)}
      </td>
      <td className="cell-pool hide-sm">
        {/Pool/.test(fixture.stage) ? fixture.stage : <span className="stage">{fixture.stage}</span>}
      </td>
      <td>
        <StatusChip code={fixture.tmsStatus} />
      </td>
    </tr>
  );
}
